/**
 * Open Orders Panel
 *
 * Live view of all pending/open orders with auto-refresh.
 * Supports per-order cancel, inline modify (price/qty), and bulk cancel-all.
 *
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  RefreshCw,
  Loader2,
  AlertCircle,
  X,
  Pencil,
  Check,
  Trash2,
  ShoppingCart,
  Clock,
} from 'lucide-react';
import { brokerAPI, type BrokerOrder } from '../../api/broker';

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

interface OpenOrdersProps {
  onOrderCancelled?: () => void;
}

interface EditState {
  orderId: string;
  qty: string;
  price: string;
}

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

const formatPrice = (val: string | null): string => {
  if (!val) return '-';
  const n = parseFloat(val);
  return isNaN(n) ? '-' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatQty = (val: string | null): string => {
  if (!val) return '-';
  const n = parseFloat(val);
  return isNaN(n) ? '-' : n.toLocaleString('en-US', { maximumFractionDigits: 4 });
};

const formatTime = (iso: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '-';
  }
};

const displayPrice = (order: BrokerOrder): string => {
  if (order.type === 'market') return 'MKT';
  if (order.limit_price) return formatPrice(order.limit_price);
  if (order.stop_price) return formatPrice(order.stop_price);
  if (order.trail_percent) return `${order.trail_percent}%`;
  if (order.trail_price) return formatPrice(order.trail_price);
  return '-';
};

// ────────────────────────────────────────────────────────────
// Skeleton rows for loading state
// ────────────────────────────────────────────────────────────

function SkeletonRows({ count = 5 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <tr key={i} className="border-b border-gray-700/50">
          {Array.from({ length: 9 }).map((_, j) => (
            <td key={j} className="px-4 py-3">
              <div className="h-4 bg-gray-700 rounded animate-pulse" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────

export default function OpenOrders({ onOrderCancelled }: OpenOrdersProps) {
  const [orders, setOrders] = useState<BrokerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<Set<string>>(new Set());
  const [cancellingAll, setCancellingAll] = useState(false);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Fetch orders ──────────────────────────────────────────

  const fetchOrders = useCallback(async () => {
    try {
      const data = await brokerAPI.getOrders('open');
      setOrders(data.orders);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch open orders');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Auto-refresh every 5 seconds ─────────────────────────

  useEffect(() => {
    fetchOrders();
    timerRef.current = setInterval(fetchOrders, 5000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchOrders]);

  // ── Cancel single order ───────────────────────────────────

  const handleCancel = async (orderId: string) => {
    setCancelling((prev) => new Set(prev).add(orderId));
    try {
      await brokerAPI.cancelOrder(orderId);
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      onOrderCancelled?.();
    } catch (err: any) {
      setError(`Cancel failed: ${err.message}`);
    } finally {
      setCancelling((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  // ── Cancel all orders ─────────────────────────────────────

  const handleCancelAll = async () => {
    if (orders.length === 0) return;
    setCancellingAll(true);
    try {
      await brokerAPI.cancelAllOrders();
      setOrders([]);
      onOrderCancelled?.();
    } catch (err: any) {
      setError(`Cancel all failed: ${err.message}`);
    } finally {
      setCancellingAll(false);
    }
  };

  // ── Start editing ─────────────────────────────────────────

  const startEdit = (order: BrokerOrder) => {
    setEditState({
      orderId: order.id,
      qty: order.qty || '',
      price: order.limit_price || order.stop_price || '',
    });
  };

  // ── Save modification ─────────────────────────────────────

  const handleSave = async () => {
    if (!editState) return;
    setSaving(true);
    try {
      const changes: Record<string, unknown> = {};
      if (editState.qty) changes.qty = parseFloat(editState.qty);
      if (editState.price) changes.limit_price = parseFloat(editState.price);
      await brokerAPI.modifyOrder(editState.orderId, changes);
      setEditState(null);
      await fetchOrders();
    } catch (err: any) {
      setError(`Modify failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────

  return (
    <div className="bg-gray-900 rounded-xl border border-gray-700/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <ShoppingCart className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-lg">Open Orders</h3>
            <p className="text-gray-400 text-xs">
              {orders.length} pending {orders.length === 1 ? 'order' : 'orders'} &middot; refreshing every 5s
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchOrders}
            className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors"
            title="Refresh now"
          >
            <RefreshCw className="w-4 h-4" />
          </button>

          {orders.length > 0 && (
            <button
              onClick={handleCancelAll}
              disabled={cancellingAll}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm font-medium transition-colors disabled:opacity-50"
            >
              {cancellingAll ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Cancel All
            </button>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-5 py-3 bg-red-500/10 border-b border-red-500/20">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
          <span className="text-red-300 text-sm">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700/50 text-gray-400 text-xs uppercase tracking-wider">
              <th className="text-left px-4 py-3 font-medium">Symbol</th>
              <th className="text-left px-4 py-3 font-medium">Side</th>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-right px-4 py-3 font-medium">Qty</th>
              <th className="text-right px-4 py-3 font-medium">Filled</th>
              <th className="text-right px-4 py-3 font-medium">Price</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Time</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-16">
                  <div className="flex flex-col items-center gap-3">
                    <Clock className="w-10 h-10 text-gray-600" />
                    <p className="text-gray-400 font-medium">No open orders</p>
                    <p className="text-gray-500 text-xs">
                      Place an order to see it here
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              orders.map((order) => {
                const isEditing = editState?.orderId === order.id;
                const isCancelling = cancelling.has(order.id);
                const isBuy = order.side?.toLowerCase() === 'buy';

                return (
                  <tr
                    key={order.id}
                    className="border-b border-gray-700/30 hover:bg-gray-800/50 transition-colors"
                  >
                    {/* Symbol */}
                    <td className="px-4 py-3 text-white font-medium">
                      {order.symbol}
                    </td>

                    {/* Side badge */}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase ${
                          isBuy
                            ? 'bg-green-500/15 text-green-400'
                            : 'bg-red-500/15 text-red-400'
                        }`}
                      >
                        {order.side?.toUpperCase() || '-'}
                      </span>
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3 text-gray-300 capitalize">
                      {order.type?.replace('_', ' ') || '-'}
                    </td>

                    {/* Qty */}
                    <td className="px-4 py-3 text-right text-gray-200 font-mono">
                      {isEditing ? (
                        <input
                          type="number"
                          step="any"
                          value={editState.qty}
                          onChange={(e) =>
                            setEditState({ ...editState, qty: e.target.value })
                          }
                          className="w-20 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-right text-white text-sm focus:outline-none focus:border-blue-500"
                        />
                      ) : (
                        formatQty(order.qty)
                      )}
                    </td>

                    {/* Filled */}
                    <td className="px-4 py-3 text-right text-gray-400 font-mono">
                      {formatQty(order.filled_qty)}
                    </td>

                    {/* Price */}
                    <td className="px-4 py-3 text-right text-gray-200 font-mono">
                      {isEditing ? (
                        <input
                          type="number"
                          step="any"
                          value={editState.price}
                          onChange={(e) =>
                            setEditState({ ...editState, price: e.target.value })
                          }
                          className="w-24 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-right text-white text-sm focus:outline-none focus:border-blue-500"
                        />
                      ) : (
                        displayPrice(order)
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/10 text-yellow-400 capitalize">
                        {order.status?.replace('_', ' ') || '-'}
                      </span>
                    </td>

                    {/* Time */}
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                      {formatTime(order.submitted_at || order.created_at)}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isEditing ? (
                          <>
                            <button
                              onClick={handleSave}
                              disabled={saving}
                              className="p-1.5 rounded bg-green-500/10 hover:bg-green-500/20 text-green-400 transition-colors disabled:opacity-50"
                              title="Save changes"
                            >
                              {saving ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Check className="w-3.5 h-3.5" />
                              )}
                            </button>
                            <button
                              onClick={() => setEditState(null)}
                              className="p-1.5 rounded bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
                              title="Cancel edit"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(order)}
                              className="p-1.5 rounded bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 transition-colors"
                              title="Modify order"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleCancel(order.id)}
                              disabled={isCancelling}
                              className="p-1.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors disabled:opacity-50"
                              title="Cancel order"
                            >
                              {isCancelling ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <X className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
